/**
 * Views/Prenomina/ModalColumnas.jsx
 *
 * Selector de columnas visibles en la tabla principal.
 *
 * Regla de columnas:
 *   - Percepciones (tipo=1): pueden mostrar Valor y/o Importe
 *   - Deducciones  (tipo=2): solo Importe (sin checkbox de Valor)
 *   - Obligaciones (tipo=3): solo Importe (sin checkbox de Valor)
 *
 * Las columnas "Otras Percepciones" y "Otras Deducciones" son fijas
 * y no aparecen aquí — siempre se muestran en la tabla.
 *
 * Sirve para dos cosas con la misma forma: las columnas de ESTA prenómina (desde
 * PreNomina) y las columnas predeterminadas de las nóminas nuevas (desde Preferencias de
 * Nómina). De ahí `titulo`, `ayuda` y `textoBoton`, que sólo cambian el texto.
 *
 * OJO: `state.config` se arma UNA sola vez, en el constructor. Quien lo use tiene que
 * montarlo condicionalmente —`{visible && <ModalColumnas .../>}`— o al reabrirlo mostrará
 * los checkboxes de la primera vez, no los guardados.
 */

import React, { Component } from 'react'
import { Tag, Checkbox, Divider } from 'antd'
import { ModalItem, ButtonItem } from '../../../../Components/Items'

const TIPO_CFG = {
  1: { label: 'Percepciones', color: 'green' },
  2: { label: 'Deducciones',  color: 'red'   },
  3: { label: 'Obligaciones', color: 'blue'  },
}

class ModalColumnas extends Component {

  constructor(props) {
    super(props)
    this.state = { config: this.initConfig(props.columnasActuales) }
  }

  initConfig(columnasActuales = []) {
    const map = {}
    for (const c of columnasActuales) {
      map[c.conceptoNominaId] = {
        mostrarValor:   c.mostrarValor   ?? false,
        mostrarImporte: c.mostrarImporte ?? true,
      }
    }
    return map
  }

  toggle(conceptoId, campo) {
    this.setState((prev) => {
      const actual = prev.config[conceptoId] || { mostrarValor: false, mostrarImporte: false }
      return { config: { ...prev.config, [conceptoId]: { ...actual, [campo]: !actual[campo] } } }
    })
  }

  handleGuardar() {
    const { config } = this.state
    const columnasVisibles = Object.entries(config)
      .filter(([, c]) => c.mostrarValor || c.mostrarImporte)
      .map(([conceptoNominaId, c]) => ({
        conceptoNominaId,
        mostrarValor:   c.mostrarValor   || false,
        mostrarImporte: c.mostrarImporte || false,
      }))
    this.props.onGuardar(columnasVisibles)
  }

  renderGrupo(tipo, lista) {
    const { config } = this.state
    const esPercepcion = tipo === 1
    const { label, color } = TIPO_CFG[tipo]

    return (
      <div key={tipo} style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 8 }}>
          <Tag color={color}>{label}</Tag>
        </div>

        {/* Encabezados */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, paddingLeft: 8, paddingRight: 8 }}>
          <span style={{ flex: 1, fontSize: 11, color: '#aaa' }}>Concepto</span>
          {esPercepcion && (
            <span style={{ width: 60, textAlign: 'center', fontSize: 11, color: '#aaa' }}>Valor</span>
          )}
          <span style={{ width: 70, textAlign: 'center', fontSize: 11, color: '#aaa' }}>Importe</span>
        </div>

        {lista.map(c => {
          const cfg    = config[c._id] || { mostrarValor: false, mostrarImporte: false }
          const activo = cfg.mostrarValor || cfg.mostrarImporte

          return (
            <div
              key={c._id}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '5px 8px', borderRadius: 4, marginBottom: 2,
                background: activo ? '#f0f8ff' : '#fff',
                border: `1px solid ${activo ? '#91caff' : '#f0f0f0'}`,
              }}
            >
              <span style={{ minWidth: 280, fontSize: 12, flex: 1 }}>
                <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#aaa', marginRight: 6 }}>
                  {c.serie}-{c.numero}
                </span>
                {c.descripcion}
              </span>

              {/* Checkbox Valor — SOLO percepciones */}
              {esPercepcion && (
                <div style={{ width: 60, textAlign: 'center' }}>
                  <Checkbox
                    checked={cfg.mostrarValor}
                    onChange={() => this.toggle(c._id, 'mostrarValor')}
                  />
                </div>
              )}

              {/* Checkbox Importe — todos los tipos */}
              <div style={{ width: 70, textAlign: 'center' }}>
                <Checkbox
                  checked={cfg.mostrarImporte}
                  onChange={() => this.toggle(c._id, 'mostrarImporte')}
                />
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  render() {
    const {
      visible, onClose, conceptosNomina,
      titulo = 'Columnas visibles en la tabla',
      ayuda = null,
      textoBoton = 'Guardar columnas',
    } = this.props
    const { config } = this.state
    const count = Object.values(config).filter(c => c.mostrarValor || c.mostrarImporte).length

    return (
      <ModalItem
        title={titulo}
        visible={visible}
        spinning={false}
        onCancel={onClose}
        width={560}
      >
        <p style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
          Elige qué mostrar para cada concepto.
        </p>
        <p style={{ fontSize: 11, color: '#aaa', marginBottom: 16 }}>
          <b>Valor</b> = cantidad (solo percepciones, ej: días, unidades de bono).
          Las columnas <b>Otras Percepciones</b> y <b>Otras Deducciones</b> son fijas y siempre visibles.
          {ayuda ? <><br />{ayuda}</> : null}
        </p>

        {[1, 2, 3].map(tipo => {
          const lista = conceptosNomina.filter(c => c.tipo === tipo)
          return lista.length ? this.renderGrupo(tipo, lista) : null
        })}

        <Divider style={{ margin: '8px 0 12px' }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 12, color: '#aaa' }}>
            {count} concepto(s) con columna activa
          </span>
          <ButtonItem type="primary" onClick={this.handleGuardar.bind(this)}>
            {textoBoton}
          </ButtonItem>
        </div>
      </ModalItem>
    )
  }
}

export default ModalColumnas